import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Bell, Send, Users, Crown, Briefcase, Globe, CheckCircle, XCircle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NotificationsPage() {
  const { token } = useAuth();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("all_users");
  const [userIds, setUserIds] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/admin/notification-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Titre et corps requis");
      return;
    }
    setSending(true);
    try {
      const payload = { title, body };
      if (target === "specific") {
        payload.user_ids = userIds.split(",").map((s) => s.trim()).filter(Boolean);
        if (!payload.user_ids.length) {
          toast.error("Ajoutez au moins un ID utilisateur");
          setSending(false);
          return;
        }
      } else {
        payload.topic = target;
      }

      const res = await fetch(`${API}/api/notifications/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Notification envoyee");
        setHistory((h) => [{ title, body, target, time: new Date().toLocaleString("fr-FR") }, ...h]);
        setTitle("");
        setBody("");
      } else {
        toast.error(data.detail || "Erreur");
      }
    } catch {
      toast.error("Erreur reseau");
    }
    setSending(false);
  };

  const topics = [
    { value: "all_users", label: "Tous les utilisateurs", icon: Globe, color: "text-blue-400" },
    { value: "arcaniens", label: "Arcaniens", icon: Crown, color: "text-amber-400" },
    { value: "organizers", label: "Organisateurs", icon: Briefcase, color: "text-green-400" },
    { value: "specific", label: "Utilisateurs specifiques", icon: Users, color: "text-purple-400" },
  ];

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center gap-3">
        <Bell className="w-7 h-7 text-[#FF6B35]" />
        <h1 className="text-2xl font-bold text-white">Push Notifications</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "FCM actifs", value: stats.fcm_tokens, icon: CheckCircle, color: "text-green-400" },
            { label: "Expo actifs", value: stats.expo_tokens, icon: CheckCircle, color: "text-blue-400" },
            { label: "Sans token", value: stats.no_token, icon: XCircle, color: "text-red-400" },
            { label: "Firebase", value: stats.firebase_enabled ? "Actif" : "Inactif", icon: Bell, color: stats.firebase_enabled ? "text-green-400" : "text-red-400" },
          ].map((s, i) => (
            <div key={i} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-[#888]">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-green-400" />
          <div>
            <p className="text-sm font-medium text-white">Rappels automatiques</p>
            <p className="text-xs text-[#888]">Notifications envoyees 24h et 1h avant chaque tournoi et partie</p>
          </div>
        </div>
        <button
          onClick={async () => {
            try {
              const res = await fetch(`${API}/api/notifications/check-reminders`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
              });
              const data = await res.json();
              if (res.ok) toast.success(data.message);
              else toast.error(data.detail || "Erreur");
            } catch { toast.error("Erreur reseau"); }
          }}
          className="px-4 py-2 bg-[#252525] hover:bg-[#333] text-white text-sm rounded-lg border border-[#444] transition-colors"
          data-testid="check-reminders-btn"
        >
          Verifier maintenant
        </button>
      </div>

      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 space-y-5">
        <h2 className="text-lg font-semibold text-white">Envoyer une notification</h2>

        <div>
          <label className="text-sm text-[#888] mb-2 block">Cible</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {topics.map((t) => (
              <button
                key={t.value}
                onClick={() => setTarget(t.value)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                  target === t.value
                    ? "border-[#FF6B35] bg-[#FF6B35]/10 text-white"
                    : "border-[#333] bg-[#121212] text-[#888] hover:border-[#555]"
                }`}
                data-testid={`topic-${t.value}`}
              >
                <t.icon className={`w-4 h-4 ${target === t.value ? "text-[#FF6B35]" : t.color}`} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {target === "specific" && (
          <div>
            <label className="text-sm text-[#888] mb-1 block">IDs utilisateurs (separes par virgule)</label>
            <input
              value={userIds}
              onChange={(e) => setUserIds(e.target.value)}
              placeholder="id1, id2, id3..."
              className="w-full bg-[#121212] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] outline-none"
              data-testid="user-ids-input"
            />
          </div>
        )}

        <div>
          <label className="text-sm text-[#888] mb-1 block">Titre</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Nouveau tournoi ce weekend !"
            className="w-full bg-[#121212] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] outline-none"
            data-testid="notif-title-input"
          />
        </div>

        <div>
          <label className="text-sm text-[#888] mb-1 block">Corps du message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Le contenu de la notification..."
            className="w-full bg-[#121212] border border-[#333] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] outline-none resize-none"
            data-testid="notif-body-input"
          />
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={handleSend}
            disabled={sending}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            data-testid="send-notification-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer
          </button>
          <div className="bg-[#252525] rounded-lg px-4 py-2 border border-[#333]">
            <p className="text-xs text-[#888]">Apercu</p>
            <p className="text-sm font-medium text-white">{title || "Titre..."}</p>
            <p className="text-xs text-[#aaa]">{body || "Corps..."}</p>
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Historique de session</h2>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between bg-[#121212] rounded-lg px-4 py-3 border border-[#252525]">
                <div>
                  <p className="text-sm font-medium text-white">{h.title}</p>
                  <p className="text-xs text-[#888]">{h.body}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#FF6B35]">{topics.find((t) => t.value === h.target)?.label || h.target}</p>
                  <p className="text-xs text-[#666]">{h.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
